/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd); see apps/admin/AGENTS.md. */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { Icon } from "./icons";

/**
 * Search-as-you-type select drawn in the kit's input frame: the ARIA 1.2 combobox
 * pattern (a text input that owns a listbox, `aria-activedescendant` moving the
 * highlight while focus stays in the input). It keeps the contract
 * screens rely on: `onQueryChange` for server search, `filter` to replace
 * matching, and `mousedown` selection so the input's blur cannot close the
 * list before the choice lands.
 *
 * The list is drawn in a portal at fixed coordinates under (or, with no room,
 * above) the field, so a card or drawer that clips its content with
 * `overflow: hidden` / `auto` can never crop it. It follows the field on
 * scroll and resize, and sits above modals (z 40), under toasts (z 60).
 */
export type SearchSelectOption<T extends string = string> = {
  value: T;
  label: string;
  /** Second line: a store, a status, an amount. */
  detail?: string;
};

type Props<T extends string> = {
  options: SearchSelectOption<T>[];
  value?: T;
  onChange: (value: T) => void;
  /** Accessible name for the field. */
  label: string;
  placeholder?: string;
  /** Shown when nothing matches. */
  emptyLabel: ReactNode;
  onQueryChange?: (query: string) => void;
  filter?: (options: SearchSelectOption<T>[], query: string) => SearchSelectOption<T>[];
  disabled?: boolean;
  /** Red frame, as on the kit's Input. */
  invalid?: boolean;
  id?: string;
};

const LIST_MAX = 240;
const GAP = 4;

/** Where the list goes for a field at `rect`: below it, or above when below is too short. */
function listPlacement(rect: DOMRect): CSSProperties {
  const below = window.innerHeight - rect.bottom - GAP - 8;
  const above = rect.top - GAP - 8;
  const up = below < Math.min(LIST_MAX, 160) && above > below;
  return {
    position: "fixed",
    left: rect.left,
    width: rect.width,
    zIndex: 45,
    ...(up
      ? { bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.min(LIST_MAX, above) }
      : { top: rect.bottom + GAP, maxHeight: Math.min(LIST_MAX, below) }),
  };
}

const defaultFilter = <T extends string>(options: SearchSelectOption<T>[], query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter(
    (option) => option.label.toLowerCase().includes(q) || (option.detail ?? "").toLowerCase().includes(q),
  );
};

export function SearchSelect<T extends string = string>({
  options,
  value,
  onChange,
  onQueryChange,
  label,
  placeholder,
  emptyLabel,
  filter = defaultFilter,
  disabled = false,
  invalid = false,
  id,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<CSSProperties | null>(null);
  const listId = useId();
  const optionId = (index: number) => `${listId}-o${index}`;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The list lives in a portal, so it is outside `ref`: a press on it
      // (its scrollbar) keeps the list open.
      if (ref.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  // Measured before paint, then kept in step with any scrolling ancestor.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      if (frameRef.current) setPlacement(listPlacement(frameRef.current.getBoundingClientRect()));
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const matches = useMemo(() => filter(options, query), [filter, options, query]);
  const selected = options.find((option) => option.value === value);

  const choose = (index: number) => {
    const option = matches[index];
    if (!option) return;
    onQueryChange?.("");
    onChange(option.value);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(0);
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((index) => (index + step + matches.length) % Math.max(matches.length, 1));
      return;
    }
    if (event.key === "Enter" && open) {
      event.preventDefault();
      choose(active);
      return;
    }
    if (event.key === "Tab") setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 0 }}>
      <div ref={frameRef} className={["rd-input", disabled && "rd-input-disabled", invalid && "rd-input-invalid"].filter(Boolean).join(" ")}>
        {/* eslint-disable-next-line no-restricted-syntax -- The kit has no combobox yet; this input is the combobox, drawn in the kit's rd-input frame. */}
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && matches.length ? optionId(active) : undefined}
          aria-label={label}
          disabled={disabled}
          // The chosen label while idle, the search while typing.
          value={open ? query : (selected?.label ?? "")}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            onQueryChange?.(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        <Icon icon={ChevronDown} size={15} color="var(--rd-text-meta)" />
      </div>

      {open && placement
        ? createPortal(
        <div
          ref={listRef}
          className="rd-root"
          dir={document.documentElement.dir || undefined}
          style={{ ...placement, display: "flex", flexDirection: "column", background: "transparent" }}
        >
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          style={{
            minHeight: 0,
            overflowY: "auto",
            margin: 0,
            padding: 5,
            listStyle: "none",
            boxSizing: "border-box",
            borderRadius: "var(--rd-radius-lg)",
            background: "var(--rd-surface)",
            boxShadow: "var(--rd-shadow-modal)",
          }}
        >
          {matches.length === 0 ? (
            <li style={{ padding: "8px 10px", fontSize: 13, color: "var(--rd-text-tertiary)" }}>{emptyLabel}</li>
          ) : (
            matches.map((option, index) => (
              <li
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={option.value === value}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(index);
                }}
                onMouseEnter={() => setActive(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: "var(--rd-radius-md)",
                  cursor: "pointer",
                  background: index === active ? "var(--rd-surface-soft)" : undefined,
                }}
              >
                <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span dir="auto" style={{ fontSize: 13, color: "var(--rd-text)" }}>
                    {option.label}
                  </span>
                  {option.detail ? (
                    <span dir="auto" className="rd-cell-sub">
                      {option.detail}
                    </span>
                  ) : null}
                </span>
                {option.value === value ? (
                  <span style={{ marginInlineStart: "auto" }}>
                    <Icon icon={Check} size={15} color="var(--rd-primary)" />
                  </span>
                ) : null}
              </li>
            ))
          )}
        </ul>
        </div>,
          document.body,
        )
        : null}
    </div>
  );
}
